import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';

import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService, BaseFilterCollapse, BaseFilterCollapseService, ConfigService } from '../../../core';
import { Periodicidade, periodicidadeMensal, periodicidadeTodoPeriodo, isPeriodicidadeTodoPeriodo } from '../../../shared';
import { DatepickerComponent, datepickerModePeriodicidade } from '../../../shared/components/datepicker';
import { ConfigEmpresa } from '../../configuracoes/app/empresa';
import { Pessoa } from '../../pessoa';
import { Representante } from '../../pessoa/representante';
import { tituloReceberSituacoes } from '../situacao/titulo-receber-situacao';
import { TituloReceberFilter } from './titulo-receber-filter';
import { TituloReceberFilterInitialization } from './titulo-receber-filter-initialization';
import { TituloReceberFilterStorageService } from './titulo-receber-filter-storage.service';

interface Config {
    isShowRepresentantes: boolean;
    isShowGerentes: boolean;
    isShowAtendentes: boolean;
    isShowCliente: boolean;
    isShowClassificacoesClientes: boolean;
}

@Component({
    selector: 'sgt-titulo-receber-filter',
    templateUrl: './titulo-receber-filter.component.html',
    styleUrls: ['././titulo-receber-filter.component.less']
})
export class TituloReceberFilterComponent implements BaseFilterCollapse, OnInit, OnChanges, AfterViewInit, OnDestroy {
    config: Config;
    configEmpresa: ConfigEmpresa;

    @Input() storageKey: string;
    @Input() filter: TituloReceberFilter;
    @Input() initialization: TituloReceberFilterInitialization;
    @Output() filterChange = new EventEmitter<any>();
    @Output() filterCleaned = new EventEmitter<any>();

    @Input() isCollapsedInDefault = false;
    isCollapsedIn = false;
    @ViewChild('filterCollapse') filterCollapse: ElementRef;
    collapseIcon = 'fas fa-caret-down';

    // Opções de visualização dos filters
    @Input() isShowRepresentantesFilter = true;
    @Input() isShowGerentesFilter = true;
    @Input() isShowAtendentesFilter = true;
    @Input() isShowClienteFilter = true;
    @Input() isShowClassificacoesClientesFilter = true;

    // Data Vigência Datepicker
    dataVigenciaDatepickerMode = datepickerModePeriodicidade.id;
    isFirstChangeDataVigencia = true;
    @ViewChild('dataVigenciaDatepicker') dataVigenciaDatepicker: DatepickerComponent;

    // Filter de pesquisa
    filterPsqSubscription: Subscription;
    isFirstChangeFilterPsq = true;
    @ViewChild('filterPsq', {static: true}) filterPsq: NgModel;

    situacoes = tituloReceberSituacoes;

    // Multiselects
    isFirstChangeLocaisFaturamentosMultiselect = true;
    isFirstChangeRepresentanteMultiselect = true;
    isFirstChangeGerenteMultiselect = true;
    isFirstChangeAtendenteMultiselect = true;
    isFirstChangeClassificacoesClientesMultiselect = true;

    constructor(private authService: AuthService,
                private configService: ConfigService,
                private baseFilterCollapseService: BaseFilterCollapseService,
                private tituloReceberFilterStorageService: TituloReceberFilterStorageService) { }

    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.setConfig();

        // Aplicando o filtro salvo no storage
        this.tituloReceberFilterStorageService.applyFilter(this.storageKey, this.filter);
        // Iniciando filtro
        this.initFilter();
    }

    private setConfig() {
        this.config = {
            isShowRepresentantes: this.isShowRepresentantesFilter && !this.authService.isRepresentante(),
            isShowGerentes: this.isShowGerentesFilter && !this.authService.isRepresentante(),
            isShowAtendentes: this.isShowAtendentesFilter && !this.authService.isRepresentante(),
            isShowCliente: this.isShowClienteFilter,
            isShowClassificacoesClientes: this.isShowClassificacoesClientesFilter
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'isCollapsedInDefault') {
                if (changes[propName].isFirstChange) {
                    setTimeout(() => {
                        this.isCollapsedIn = changes[propName].currentValue;
                    }, 100);
                }
            }
        }
    }

    ngAfterViewInit() {
      if (!this.isCollapsedInDefault) {
        jQuery(this.filterCollapse.nativeElement).css('display', 'none');
      }
    }

    /** Inicializa o filtro */
    private initFilter() {
        // Filter de pesquisa
        this.subscribeToFilterPsq();

        setTimeout(() => {
            // Setando o mês atual quando o filtro não possuir a periodicidade de vigência
            if (!this.filter.possuiPeriodicidadeVigencia()) {
                if (this.filter.isPeriodicidadeTodoPeriodo()) {
                    this.setTodoPeriodo();
                } else {
                    this.setMesVigenciaAtual();
                }
            }

            this.initialization.initializeMain();
        }, 200);
    }

    // Realiza o subscribe nas alterações feitas no input do filtro da pesquisa
    private subscribeToFilterPsq() {
        this.filterPsqSubscription = this.filterPsq.valueChanges
            .pipe(
                debounceTime(1000), // Dando um debounce de 1000ms para emitir a alteração
                map(emittedValue => emittedValue?.trim()),
                distinctUntilChanged()
            )
            .subscribe(() => {
                if (this.isFirstChangeFilterPsq) {
                    this.isFirstChangeFilterPsq = false;
                    return;
                }

                if (this.filter.numeroErp?.trim().length > 0) {
                    this.filter.isFilterDirty = true;
                }

                this.filterChange.emit();
            });
    }

    /** Abre/Fecha o filtro */
    toggleCollapse() {
        this.baseFilterCollapseService.toggleCollapse(this);
    }

    /** Abre o filtro */
    open() {
        this.baseFilterCollapseService.open(this);
    }

    /** Fecha o filtro */
    close() {
        this.baseFilterCollapseService.close(this);
    }

    /** Limpa o filtro */
    clear() {
        this.baseFilterCollapseService.clear(this);
        this.close();
        this.filterCleaned.emit();
    }

    /** Emite evento de alteração no filtro para atualizar a pesquisa */
    refresh() {
        this.close();
        this.filterChange.emit();
    }

    /** Seta o foco no datepicker da data de vigência */
    setFocusDataVigenciaDatepicker() {
        this.dataVigenciaDatepicker?.setFocus();
    }

    /** Seta todos o período na periodicidade da data */
    setTodoPeriodo() {
        this.filter.periodicidadeVigencia = periodicidadeTodoPeriodo.id;
        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeTodoPeriodo);
    }

    /** Seta a periodicidade mensal na data */
    setPeriodicidadeMensal() {
        this.filter.periodicidadeVigencia = periodicidadeMensal.id;
        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeMensal, this.filter.dataInicioVigencia);
    }

    /** Seta a data do filtro com periodicidade mensal e com o mês atual */
    setMesVigenciaAtual() {
        const data = new Date();
        data.setDate(1); // Setando sempre o dia 1;

        this.filter.periodicidadeVigencia = periodicidadeMensal.id;
        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeMensal, data);
    }

    /** Seta a periodicidade da vigência no filtro da data de vigência */
    setVigenciaAtual(periodicidadeVigencia: Periodicidade) {
        const data = new Date();
        data.setDate(1); // Setando sempre o dia 1;

        this.dataVigenciaDatepicker?.setPeriodicidade(periodicidadeVigencia, data);
    }

    /** Ao alterar a referência da data */
    onChangeReferenciaData() {
        this.filterChange.emit();
    }

    /** Ao alterar a data de vigência */
    onChangeDataVigencia() {
        if (this.isFirstChangeDataVigencia) {
            this.isFirstChangeDataVigencia = false;
            return;
        }

        this.filterChange.emit();
    }

    /** Ao alterar a data de início do período de vigência */
    onChangeDataInicioVigenciaPeriodo() {
        this.filterChange.emit();
    }

    /** Ao alterar a data de término do período de vigência */
    onChangeDataTerminoVigenciaPeriodo() {
        this.filterChange.emit();
    }

    /** Ao alterar os locais de faturamentos */
    onChangeLocaisFaturamentos() {
        if (this.isFirstChangeLocaisFaturamentosMultiselect) {
            this.isFirstChangeLocaisFaturamentosMultiselect = false;
            return;
        }
        this.filter.isFilterDirty = true;
    }

    /** Ao alterar as situações */
    onChangeSituacoes() {
        this.filterChange.emit();
    }

    /** Ao alterar os representantes */
    onChangeRepresentantes() {
        if (this.isFirstChangeRepresentanteMultiselect) {
            this.isFirstChangeRepresentanteMultiselect = false;
            return;
        }

        this.filterChange.emit();
    }

    /** Ao alterar os gerentes */
    onChangeGerentes() {
        if (this.isFirstChangeGerenteMultiselect) {
            this.isFirstChangeGerenteMultiselect = false;
            return;
        }

        this.filter.isFilterDirty = true;
        this.filterChange.emit();
    }

    /** Ao alterar os atendentes */
    onChangeAtendentes() {
        if (this.isFirstChangeAtendenteMultiselect) {
            this.isFirstChangeAtendenteMultiselect = false;
            return;
        }

        this.filter.isFilterDirty = true;
        this.filterChange.emit();
    }

    /** Ao alterar as classificações de clientes */
    onChangeClassificacoesClientes() {
        if (this.isFirstChangeClassificacoesClientesMultiselect) {
            this.isFirstChangeClassificacoesClientesMultiselect = false;
            return;
        }

        this.filter.isFilterDirty = true;
        this.filterChange.emit();
    }

    /** Ao alterar o cliente/pessoa no typeahead */
    onTypeaheadChangeCliente(pessoa: Pessoa) {
        if (pessoa) {
            this.filter.idsPessoas = [pessoa.id];
            this.filter.isFilterDirty = true;
        } else {
            this.filter.idsPessoas = [];
        }
    }

    /** Ao alterar o representante no typeahead */
    onTypeaheadChangeRepresentante(representante: Representante) {
        if (representante) {
            this.filter.idRepresentante = representante.id;
            this.filter.isFilterDirty = true;
        } else {
            this.filter.idRepresentante = null;
        }
    }

    ngOnDestroy() {
        this.filterPsqSubscription?.unsubscribe();
    }
}
